import type { Dispatch, SetStateAction } from 'react';
import type { User } from 'firebase/auth';
import type { GalleryItem, PendingTileState } from './types';
import type { UploadProgress } from '@/api/parsnipApi';

export const validateVideoFiles = (files: File[]): string | null => {
  const invalidFiles = files.filter(file => !file.type.startsWith('video/'));
  return invalidFiles.length > 0 ? 'Please upload only video files' : null;
};

export const validateAnalysisInputs = (
  currentUser: User | null,
  contentFilesLength: number,
  resultFilesLength: number,
): string | null => {
  if (!currentUser) return 'Please sign in to analyze videos';
  if (contentFilesLength === 0) return 'Please upload a content video';
  if (resultFilesLength === 0) return 'Please upload at least one result video';
  return null;
};

export function buildContentProgressCb(
  id: string,
  setPending: Dispatch<SetStateAction<PendingTileState | null>>,
) {
  return (progress: UploadProgress) => {
    setPending((prev) => prev?.id === id ? { 
      ...prev, 
      contentProgress: progress.percent,
      contentEta: progress.eta,
      contentSpeed: progress.mbps,
    } : prev);
  };
}

export function buildResultProgressCb(
  id: string,
  idx: number,
  setPending: Dispatch<SetStateAction<PendingTileState | null>>,
) {
  return (progress: UploadProgress) => {
    setPending((prev) => {
      if (prev?.id !== id) return prev;
      const updatedProgress = [...prev.resultProgresses];
      updatedProgress[idx] = progress.percent;
      
      const updatedEtas = [...(prev.resultEtas || [])];
      updatedEtas[idx] = progress.eta;
      
      const updatedSpeeds = [...(prev.resultSpeeds || [])];
      updatedSpeeds[idx] = progress.mbps;
      
      return { 
        ...prev, 
        resultProgresses: updatedProgress,
        resultEtas: updatedEtas,
        resultSpeeds: updatedSpeeds,
      };
    });
  };
}

export function patchThumbnailInItems(
  prev: GalleryItem[],
  videoId: string,
  thumbnailUrl: string,
): GalleryItem[] {
  return prev.map((item) => {
    if (item.content_video_id === videoId) {
      return { ...item, content_thumbnail_url: thumbnailUrl };
    }
    const resultIdx = item.result_video_ids?.indexOf(videoId) ?? -1;
    if (resultIdx !== -1) {
      const newThumbs = [...(item.result_thumbnail_urls ?? [])];
      newThumbs[resultIdx] = thumbnailUrl;
      return { ...item, result_thumbnail_urls: newThumbs };
    }
    return item;
  });
}
